/**
 * StrayHeroSlider
 * ───────────────────────────────────────────────────────────────
 * Handles:
 *   - Cross-fade slide transitions
 *   - Auto-play with configurable speed
 *   - Pause on hover / focus / reduced-motion preference
 *   - Progress bar pagination dots
 *   - Lazy loading of off-screen media
 *   - YouTube iframe API autoplay on active slide
 *   - Native video play/pause per slide visibility
 *   - Keyboard navigation (←/→ arrows)
 *   - Touch/swipe support
 *   - Shopify Theme Editor block select awareness
 */

class StrayHeroSlider extends HTMLElement {
  constructor() {
    super();

    this.slides      = [];
    this.dots        = [];
    this.current     = 0;
    this.total       = 0;
    this.timer       = null;
    this.isPaused    = false;
    this.isAnimating = false;

    // Config from data attributes (set by Liquid)
    this.autoplay     = this.dataset.autoplay !== 'false';
    this.speed        = parseInt(this.dataset.speed, 10)       || 5000;  // ms between slides
    this.fadeDuration = parseInt(this.dataset.fadeDuration, 10) || 800;  // ms cross-fade
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.stray-hero__slide'));
    this.dots   = Array.from(this.querySelectorAll('.stray-hero__page-dot'));
    this.total  = this.slides.length;

    if (this.total === 0) return;

    // Apply CSS token for progress bar animation duration
    this.style.setProperty('--autoplay-speed', `${this.speed}ms`);
    this.style.setProperty('--slide-duration',  `${this.fadeDuration}ms`);

    // Activate first slide
    this._activateSlide(0, false);

    if (this.total === 1) return; // no need for any interaction handling

    // Pause on hover
    this.addEventListener('mouseenter', () => this._pause());
    this.addEventListener('mouseleave', () => this._resume());

    // Pause on focus within (keyboard users)
    this.addEventListener('focusin',  () => this._pause());
    this.addEventListener('focusout', () => this._resume());

    // Keyboard navigation
    this.addEventListener('keydown', this._onKeyDown.bind(this));
    this.setAttribute('tabindex', '0');

    // Touch / swipe
    this._initSwipe();

    // Pagination dot clicks
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this._goTo(i));
    });

    // Start auto-play
    if (this.autoplay && !this._prefersReducedMotion()) {
      this._startTimer();
    }

    // Shopify Theme Editor
    this._initThemeEditor();
  }

  disconnectedCallback() {
    this._clearTimer();
    window.removeEventListener('scroll', this._scrollHandler);
  }

  // ── Slide activation ──────────────────────────────────────

  _activateSlide(index, animate = true) {
    if (this.isAnimating && animate) return;
    if (index === this.current && animate) return;

    this.isAnimating = true;

    const prev = this.slides[this.current];
    const next = this.slides[index];
    
    // Check if this is the very first initialization
    const isInitialLoad = (prev === next);

    // Pause video/YouTube on leaving slide
    if (!isInitialLoad) {
      this._pauseSlideMedia(prev);
    }

    // Swap classes
    if (!isInitialLoad) {
      prev.classList.remove('is-active');
      prev.classList.add('is-prev');
    }
    
    next.classList.add('is-active');

    // Update dots
    this.dots.forEach((d, i) => {
      d.classList.toggle('is-active', i === index);
      // Reset fill by forcing reflow
      const fill = d.querySelector('.stray-hero__page-dot-fill');
      if (fill) {
        fill.style.transition = 'none';
        fill.style.height = '0%';
        // Trigger reflow
        fill.getBoundingClientRect();
        fill.style.transition = '';
      }
    });

    // Restart Ken Burns on active slide
    this._restartAnimation(next);

    // Play media on new active slide
    this._playSlideMedia(next);

    this.current = index;

    // Allow next transition after fade completes
    if (!isInitialLoad && animate) {
      setTimeout(() => {
        prev.classList.remove('is-prev');
        this.isAnimating = false;
      }, this.fadeDuration + 50);
    } else {
      this.isAnimating = false;
    }
  }

  _goTo(index) {
    this._clearTimer();
    this._activateSlide(index);
    if (this.autoplay && !this.isPaused && !this._prefersReducedMotion()) {
      this._startTimer();
    }
  }

  _next() {
    const nextIndex = (this.current + 1) % this.total;
    this._activateSlide(nextIndex);
  }

  _prev() {
    const prevIndex = (this.current - 1 + this.total) % this.total;
    this._activateSlide(prevIndex);
  }

  // ── Timer ─────────────────────────────────────────────────

  _startTimer() {
    this._clearTimer();
    this.timer = setInterval(() => {
      if (!this.isPaused) this._next();
    }, this.speed);
  }

  _clearTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  _pause() {
    this.isPaused = true;
  }

  _resume() {
    this.isPaused = false;
  }

  // ── Media helpers ─────────────────────────────────────────

  _playSlideMedia(slide) {
    if (!slide) return;

    // Native video
    const video = slide.querySelector('video');
    if (video) {
      video.play().catch(() => {}); // suppress autoplay policy errors
    }

    // YouTube iframe — send postMessage play command
    const iframe = slide.querySelector('iframe[data-yt]');
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'playVideo' }),
        '*'
      );
    }
  }

  _pauseSlideMedia(slide) {
    if (!slide) return;

    const video = slide.querySelector('video');
    if (video) video.pause();

    const iframe = slide.querySelector('iframe[data-yt]');
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo' }),
        '*'
      );
    }
  }

  // ── Ken Burns restart ──────────────────────────────────────

  _restartAnimation(slide) {
    const media = slide.querySelector('img, video');
    if (!media) return;
    // Force animation restart by toggling the element
    media.style.animation = 'none';
    media.getBoundingClientRect(); // reflow
    media.style.animation = '';
  }

  // ── Keyboard ──────────────────────────────────────────────

  _onKeyDown(e) {
    if (e.key === 'ArrowRight') { e.preventDefault(); this._goTo((this.current + 1) % this.total); }
    if (e.key === 'ArrowLeft')  { e.preventDefault(); this._goTo((this.current - 1 + this.total) % this.total); }
  }

  // ── Touch / swipe ─────────────────────────────────────────

  _initSwipe() {
    let startX = 0;
    let startY = 0;

    this.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
        dx < 0 ? this._goTo((this.current + 1) % this.total)
               : this._goTo((this.current - 1 + this.total) % this.total);
      }
    }, { passive: true });
  }

  // ── Reduced motion ────────────────────────────────────────

  _prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // ── Shopify Theme Editor ──────────────────────────────────

  _initThemeEditor() {
    if (!window.Shopify || !Shopify.designMode) return;

    document.addEventListener('shopify:block:select', (e) => {
      const slide = e.target.closest('.stray-hero__slide');
      if (!slide || !this.contains(slide)) return;
      const index = this.slides.indexOf(slide);
      if (index > -1) {
        this._clearTimer();
        this._activateSlide(index);
      }
    });

    document.addEventListener('shopify:block:deselect', (e) => {
      if (!this.contains(e.target)) return;
      if (this.autoplay) this._startTimer();
    });
  }
}

customElements.define('stray-hero-slider', StrayHeroSlider);